// src/oura/components/visibility/UncoveredSearches.tsx
//
// The searches the 50 questions do not cover (#3878, ourteam-backend#4981).
// Both roles see it. Only staff act on it: which slot to swap is the
// operator's decision, so the merchant reads the searches and nothing else.
//
// The set is a recipe, and Google knows what the recipe missed. Each row
// carries the backend's own candidate: which slot variable the search would
// fill, what it would replace, and whether the recipe allows it at all.
//
// Adding swaps ONE slot's fill at the next scan: the set stays 50, the slot
// keeps its number, group and check, and the trend marks the break because a
// changed question is a different measurement. Five per scan at most, which
// the server enforces; this block reports that refusal rather than hiding it.

import { useEffect, useMemo, useState } from "react";
import { getQueuedFills, queueQuestionFill, unqueueQuestionFill, type AeoQueuedFill, type AeoSearchQuestions, type AeoSearchRow } from "@service/aeo";
import { PillButton, RankChip, SURFACE, TYPE, Tag } from "@oura/ui";
import { cn } from "../../lib/cn";
import type { StageRefreshJob } from "../../hooks/useRefreshJob";
import { CardRefresh, RefreshProgress } from "../RefreshControl";
import { SEARCH_REFRESH } from "../../refreshJobs";
import { perYear, SEARCHES_NOT_BUILT_SENTENCE, searchesAbsentSentence, searchesNotBuilt, uncoveredSearches } from "../../topSearches";

export function UncoveredSearches({
    staff,
    block,
    businessId,
    searchJob,
}: {
    staff: boolean;
    block?: AeoSearchQuestions | null;
    /** Needed to queue a slot re-fill. Absent on a past scan: read only. */
    businessId?: string;
    /** The shared Search Console pull (#3907). Absent means no control. */
    searchJob?: StageRefreshJob;
}) {
    const rows = useMemo(() => uncoveredSearches(block), [block]);
    const [queued, setQueued] = useState<AeoQueuedFill[]>([]);
    const [busy, setBusy] = useState<string | null>(null);
    const [note, setNote] = useState<string | null>(null);
    // A 403 means this session may not queue at all; the buttons go rather
    // than failing once per row.
    const [writeBlocked, setWriteBlocked] = useState(false);

    useEffect(() => {
        if (!staff || !businessId || rows.length === 0) return;
        let live = true;
        void getQueuedFills(businessId).then((q) => { if (live) setQueued(q); });
        return () => { live = false; };
    }, [staff, businessId, rows.length]);

    // Nothing to offer, and the payload says why (#3897). The same sentence
    // the Search performance card uses, so the two never tell different
    // stories about one empty block. Silent when there is no stated reason.
    const why = rows.length === 0 ? searchesAbsentSentence(block) : null;
    // The scan has no search block and staff can pull it: keep the card, with
    // the pull control, so a just-connected Search Console is not a dead end.
    if (rows.length === 0 && !why && staff && searchJob && searchesNotBuilt(block)) {
        return (
            <section aria-labelledby="uncovered-searches" className="border-t border-gray-100 px-5 pb-5 pt-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <h3 id="uncovered-searches" className={cn(TYPE.tt, "m-0 text-gray-900")}>{"Top searches the 50 don't cover"}</h3>
                    <CardRefresh job={searchJob} spec={SEARCH_REFRESH} />
                </div>
                <div className="mb-3 space-y-1"><RefreshProgress job={searchJob} runningText={SEARCH_REFRESH.running} /></div>
                <p className={cn(TYPE.tb, "m-0 text-gray-600")}>{SEARCHES_NOT_BUILT_SENTENCE}</p>
            </section>
        );
    }
    if (rows.length === 0 && !why) return null;

    const canQueue = staff && !!businessId && !writeBlocked;
    /**
     * Keyed on the SLOT, then compared on the value. Several searches compete
     * for one slot, and matching on the variable alone announced "Queued" on
     * the rivals of the one queued. A slot held by a DIFFERENT value is the
     * reason this row cannot be queued until the other is undone.
     */
    const queuedFor = (variable: string) => queued.find((q) => q.variable === variable);

    const add = async (row: AeoSearchRow) => {
        const c = row.candidate;
        if (!businessId || !c || !c.allowed) return;
        setBusy(c.variable);
        setNote(null);
        const r = await queueQuestionFill(businessId, { variable: c.variable, value: c.value, replaces: c.replaces });
        setBusy(null);
        if (r.ok) {
            setQueued((q) => [...q, { variable: c.variable, value: c.value, replaces: c.replaces }]);
            return;
        }
        if (r.kind === "forbidden" || r.kind === "absent") { setWriteBlocked(true); return; }
        // A full queue is the rule working, not a failure.
        setNote(r.kind === "full" ? "Five changes are already queued for the next scan. Undo one to queue another." : r.message);
    };

    const remove = async (variable: string) => {
        if (!businessId) return;
        setBusy(variable);
        const ok = await unqueueQuestionFill(businessId, variable);
        setBusy(null);
        if (ok) setQueued((q) => q.filter((x) => x.variable !== variable));
        else setNote("That change could not be taken off the queue.");
    };

    return (
        <section aria-labelledby="uncovered-searches" className="border-t border-gray-100 px-5 pb-5 pt-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <h3 id="uncovered-searches" className={cn(TYPE.tt, "m-0 text-gray-900")}>{"Top searches the 50 don't cover"}</h3>
                <div className={cn(TYPE.tc, "flex flex-wrap items-center gap-2.5 text-gray-500")}>
                    {queued.length > 0 && <Tag tone="blue">{queued.length} queued for the next scan</Tag>}
                    {staff && <span>Up to 5 per scan. A swap breaks the line.</span>}
                    {searchJob && <CardRefresh job={searchJob} spec={SEARCH_REFRESH} />}
                </div>
            </div>
            {searchJob && <div className="mb-3 space-y-1"><RefreshProgress job={searchJob} runningText={SEARCH_REFRESH.running} /></div>}
            {note && <p className={cn(TYPE.tc, "m-0 mb-3 text-amber-800")}>{note}</p>}
            {why ? (
                <p className={cn(TYPE.tb, "m-0 text-gray-600")}>{why}</p>
            ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {rows.map((row) => {
                        const c = row.candidate;
                        const held = c ? queuedFor(c.variable) : undefined;
                        const queuedHere = !!c && !!held && held.value === c.value;
                        const slotTaken = !!c && !!held && held.value !== c.value;
                        return (
                            <div key={row.query} className={cn(SURFACE.inner, "flex items-center gap-3")}>
                                <div className="min-w-0 flex-1">
                                    <p className={cn(TYPE.tb, "m-0 truncate font-medium text-gray-900")} title={row.query}>{row.query}</p>
                                    <div className="mt-1 flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1">
                                        <RankChip rank={row.rank} />
                                        {/* The recipe's own sentence when it refuses, verbatim:
                                            a rewrite here would be the screen inventing a rule.
                                            Two lines, so the reason is read, not cut; under the
                                            chip when the card is too narrow for both. */}
                                        <span className={cn(TYPE.tm, "line-clamp-2 min-w-[10rem] flex-1 text-gray-500")}>
                                            {perYear(row.searches)}
                                            {staff && (
                                                <>
                                                    {" · "}
                                                    {c ? (c.allowed ? (c.replaces ? `Replaces "${c.replaces}"` : `Fills ${c.variable.replace(/_/g, " ")}`) : c.reason) : "No slot in the recipe takes it"}
                                                </>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                {staff && <div className={cn(TYPE.tm, "flex shrink-0 items-center gap-2 whitespace-nowrap text-gray-400")}>
                                    {!c ? (
                                        "No slot to fill"
                                    ) : !c.allowed ? (
                                        "Page task"
                                    ) : slotTaken ? (
                                        <span title={`"${held?.value}" is queued for this slot. Undo it to queue this search instead.`}>Slot already queued</span>
                                    ) : !canQueue ? (
                                        writeBlocked ? "Not open to this login" : "Add from the latest scan"
                                    ) : queuedHere ? (
                                        <>
                                            <span className="font-medium text-blue-700">Queued</span>
                                            <PillButton variant="text" size="sm" writes disabled={busy === c.variable} onClick={() => void remove(c.variable)}>
                                                Undo
                                            </PillButton>
                                        </>
                                    ) : (
                                        <PillButton size="sm" busy={busy === c.variable} writes onClick={() => void add(row)}>
                                            Add
                                        </PillButton>
                                    )}
                                </div>}
                            </div>
                        );
                    })}
                </div>
            )}
            <p className={cn(TYPE.tm, "m-0 mt-3 text-gray-400")}>
                {staff
                    ? "Applied at the next scan and marked on the trend, because a changed question is a different measurement. The set stays 50 and the types keep their counts."
                    : "What people search on Google that your 50 questions do not ask yet. ourteam picks which to add, and a swapped question starts a new line on the chart."}
            </p>
        </section>
    );
}

/**
 * Above the question rows when NO question carries a search chip: the rows
 * then show no empty chip at all, and this says why in one place and, for
 * staff, puts the one action that fixes it where the rows are. The card
 * below the table repeats the control, but it sits under the pagination.
 *
 * Silent as soon as any question has a search: a few empty chips among real
 * ones are individual unmeasured questions, and the chip's own tooltip says so.
 */
export function SearchesEmptyNotice({
    staff,
    empty,
    anySearch,
    searchJob,
}: {
    staff: boolean;
    empty: { label: string; why: string; notBuilt: boolean };
    anySearch: boolean;
    searchJob?: StageRefreshJob;
}) {
    if (anySearch) return null;
    const canPull = staff && !!searchJob && empty.notBuilt;
    return (
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-3">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="min-w-0 flex-1 basis-72">
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>No Google searches are matched to these questions</p>
                    <p className={cn(TYPE.tm, "m-0 mt-0.5 text-gray-600")}>
                        {empty.why}
                        {canPull && " Pull Search Console again to build them. No AI assistants are asked."}
                        {staff && empty.notBuilt && !searchJob && " Open the latest scan to pull Search Console again."}
                    </p>
                </div>
                {canPull && searchJob && <CardRefresh job={searchJob} spec={SEARCH_REFRESH} />}
            </div>
            {searchJob && <div className="mt-2 space-y-1"><RefreshProgress job={searchJob} runningText={SEARCH_REFRESH.running} /></div>}
        </div>
    );
}
